import { FieldValues } from 'react-hook-form'
import { UpdateMerchantApplicationMutationInput } from 'generated/graphql'
import { toNumericString, toNumericInt } from './transform-utils'

export const mapFormToMerchantApplication = (
  data: FieldValues
): UpdateMerchantApplicationMutationInput => {
  const businessStructureDetails = data.businessStructureDetails ?? {}
  const taxInfo = data.taxInfo ?? {}
  const businessActivity = data.businessActivity ?? {}
  const businessProcessingInfo = data.businessProcessingInfo ?? {}
  const fulfillmentDetails = data.fulfillmentDetails ?? {}

  const registeredBusinessAddress = {
    addressLine:
      businessStructureDetails.registeredBusinessAddress?.addressLine ?? null,
    addressLine2:
      businessStructureDetails.registeredBusinessAddress?.addressLine2 ?? null,
    city: businessStructureDetails.registeredBusinessAddress?.city ?? null,
    zip: businessStructureDetails.registeredBusinessAddress?.zip ?? null,
    state: businessStructureDetails.registeredBusinessAddress?.state ?? null,
    countryAlpha2:
      businessStructureDetails.registeredBusinessAddress?.countryAlpha2 ?? null
  }

  const physicalBusinessAddress =
    businessStructureDetails.sameAsRegisteredAddress
      ? registeredBusinessAddress
      : {
          addressLine:
            businessStructureDetails.physicalBusinessAddress?.addressLine ??
            null,
          addressLine2:
            businessStructureDetails.physicalBusinessAddress?.addressLine2 ??
            null,
          city: businessStructureDetails.physicalBusinessAddress?.city ?? null,
          zip: businessStructureDetails.physicalBusinessAddress?.zip ?? null,
          state:
            businessStructureDetails.physicalBusinessAddress?.state ?? null,
          countryAlpha2:
            businessStructureDetails.physicalBusinessAddress?.countryAlpha2 ??
            null
        }

  const taxAddress = taxInfo.sameAsRegisteredAddress
    ? registeredBusinessAddress
    : {
        addressLine: taxInfo.address?.addressLine ?? null,
        addressLine2: taxInfo.address?.addressLine2 ?? null,
        city: taxInfo.address?.city ?? null,
        zip: taxInfo.address?.zip ?? null,
        state: taxInfo.address?.state ?? null,
        countryAlpha2: taxInfo.address?.countryAlpha2 ?? null
      }

  return {
    businessDetails: {
      legalBusinessName: data.businessDetails?.legalBusinessName ?? null,
      doingBusinessAs: data.businessDetails?.doingBusinessAs ?? null,
      industryDescription: data.businessDetails?.industryDescription ?? null
    },
    businessStructureDetails: {
      registeredBusinessAddress,
      physicalBusinessAddress,
      businessPhoneNumber: businessStructureDetails.businessPhoneNumber ?? null,
      dbaPhoneNumber: businessStructureDetails.dbaPhoneNumber ?? null,
      dateOfIncorporation:
        businessStructureDetails.dateOfIncorporation?.toString() ?? null,
      businessStructure: businessStructureDetails.businessStructure ?? null,
      businessEmail: businessStructureDetails.businessEmail ?? null,
      publiclyTradedCorpExchange:
        businessStructureDetails.publiclyTradedCorpExchange ?? null,
      publiclyTradedCorporationSymbol:
        businessStructureDetails.publiclyTradedCorporationSymbol ?? null,
      businessType: businessStructureDetails.businessType ?? null,
      businessTypeDescription:
        businessStructureDetails.businessTypeDescription ?? null,
      physicalAddrSameAsRegistered:
        businessStructureDetails.sameAsRegisteredAddress ?? null
    },
    customerSupport: {
      phone: data.customerSupport?.phone ?? null
    },
    taxInfo: {
      taxpayerName: taxInfo.taxpayerName ?? null,
      taxpayerIdNumber: taxInfo.taxpayerIdNumber ?? null,
      taxpayerIdType: taxInfo.taxpayerIdType ?? null,
      address: taxAddress,
      exemptPayee: taxInfo.exemptPayee ?? null,
      taxExempt501c: taxInfo.taxExempt501c ?? null,
      electronicTaxDocumentConsent:
        taxInfo.electronicTaxDocumentConsent ?? null,
      taxAddressSameAsRegisteredBusinessAddress:
        taxInfo.sameAsRegisteredAddress ?? null
    },
    websites:
      data.websites?.map(
        (w: {
          domain: string
          login?: string
          password?: string
          platformRequiresTestUser?: boolean
        }) => ({
          domain: w.domain,
          login: w.login ?? null,
          password: w.password ?? null,
          platformRequiresTestUser: w.platformRequiresTestUser ?? null
        })
      ) ?? null,
    businessActivity: {
      ecommerceBasisPoints: toNumericString(
        businessActivity.ecommerceBasisPoints,
        null
      ),
      manuallyKeyedCnpMotoBasisPoints: toNumericString(
        businessActivity.manuallyKeyedCnpMotoBasisPoints,
        null
      ),
      manuallyKeyedCpImprintBasisPoints: toNumericString(
        businessActivity.manuallyKeyedCpImprintBasisPoints,
        null
      ),
      cardPresentBasisPoints: toNumericString(
        businessActivity.cardPresentBasisPoints,
        null
      ),
      businessToBusinessBasisPoints: toNumericString(
        businessActivity.businessToBusinessBasisPoints,
        null
      ),
      monthlyCardVolumeAmount: toNumericString(
        businessActivity.monthlyCardVolumeAmount,
        null
      ),
      maximumMonthlyCardVolumeAmount: toNumericString(
        businessActivity.maximumMonthlyCardVolumeAmount,
        null
      ),
      averageTransactionAmount: toNumericString(
        businessActivity.averageTransactionAmount,
        null
      ),
      maximumTransactionAmount: toNumericString(
        businessActivity.maximumTransactionAmount,
        null
      ),
      annualAmexVolumeExceeds: businessActivity.annualAmexVolumeExceeds ?? null,
      esaNumber: businessActivity.esaNumber ?? null,
      requestingPayoutsToCustomers:
        businessProcessingInfo.requestingPayoutsToCustomers ?? null,
      seasonalBusiness: businessActivity.seasonalBusiness ?? null,
      seasonalBusinessMonths: businessActivity.seasonalBusinessMonths ?? null
    },
    businessProcessingInfo: {
      numberOfPhysicalLocations: toNumericInt(
        businessProcessingInfo.numberOfPhysicalLocations,
        null
      ),
      applyingForAdditionalLocations:
        businessProcessingInfo.applyingForAdditionalLocations ?? null,
      isWebsitePciCompliant:
        businessProcessingInfo.isWebsitePciCompliant ?? null,
      hasAcceptedCreditCardsBefore:
        businessProcessingInfo.hasAcceptedCreditCardsBefore ?? null,
      hasAcceptedCcBeforeDescription:
        businessProcessingInfo.hasAcceptedCcBeforeDescription ?? null,
      terminatedByPreviousProcessor:
        businessProcessingInfo.terminatedByPreviousProcessor ?? null,
      hasExistingPaycomMerchant:
        businessProcessingInfo.hasExistingPaycomMerchant ?? null,
      existingPaycomMerchantId:
        businessProcessingInfo.existingPaycomMerchantId ?? null,
      returnAndRefundPolicy:
        businessProcessingInfo.returnAndRefundPolicy ?? null,
      wantsAmexMarketingMaterials:
        businessProcessingInfo.wantsAmexMarketingMaterials ?? null
    },
    fulfillmentDetails: {
      fulfillmentMethod: fulfillmentDetails.fulfillmentMethod ?? null,
      fulfillmentMethodDescription:
        fulfillmentDetails.fulfillmentMethodDescription ?? null,
      deliveryTimeframe: fulfillmentDetails.deliveryTimeframe ?? null
    }
  }
}
